import { CreateDropDownButton, CreateArraySelector } from "./CustomControls.js";
import * as Bridge from "./BridgeLogic.js";
import { Localization } from "./Localization.js";

// --- Player Selection Logic ---

const PLAYER_IDS = [
    { prefix: "north", label: () => Localization.NorthPlayer },
    { prefix: "west", label: () => Localization.WestPlayer },
    { prefix: "east", label: () => Localization.EastPlayer },
    { prefix: "south", label: () => Localization.SouthPlayer }
];

function getPlayers(): string[]
{
    return ["Коля", "Саша", "Вова", "Денис"];
}

function updateStartButtonState(): void
{
    const allPlayers = new Set(getPlayers());
    const selectedPlayers = new Set<string>();
    for (const { prefix } of PLAYER_IDS)
    {
        const text = document.getElementById(`${prefix}-selector`)?.innerText;
        if (text && allPlayers.has(text)) selectedPlayers.add(text);
    }
    const btn = document.getElementById("start-btn") as HTMLButtonElement;
    if (btn) btn.disabled = selectedPlayers.size < allPlayers.size;
}

function createSideSelector(idPrefix: string, title: string): void
{
    const titleDiv = document.getElementById(`${idPrefix}-title`) as HTMLDivElement;
    titleDiv.innerText = title;
    const dropdown = CreateDropDownButton(`${idPrefix}-selector`, Localization.ChooseButton, getPlayers());
    $(dropdown).find('a').on('click', updateStartButtonState);
    const selectorDiv = document.getElementById(`${idPrefix}-selector-div`) as HTMLDivElement;
    selectorDiv.appendChild(dropdown);
}

// --- Points Table Logic ---

class HTMLPointsRow
{
    PointsRow: HTMLTableRowElement;

    constructor(element: HTMLElement)
    {
        this.PointsRow = $(element).closest(".points-row")[0] as HTMLTableRowElement;
    }

    static #createContractLevelSelector()
    {
        const ContractLevels = ['1', '2', '3', '4', '5', '6', '7'];
        const result = CreateArraySelector(ContractLevels, "contract-selector", (level: string) => level);
        const select = $(result).find('select')[0] as HTMLSelectElement;
        select.onchange = e => (new HTMLPointsRow(e.target as HTMLElement)).#updateDealResults();
        select.id = "contract-level-selector";
        const cell = document.createElement("td") as HTMLTableCellElement;
        cell.appendChild(result);
        return cell;
    }

    static #createContractSuitSelector()
    {
        const result = CreateArraySelector(
            Bridge.Suits,
            "contract-selector",
            suit => suit.DisplayName,
            suit => suit.Color
        );
        const select = $(result).find('select')[0] as HTMLSelectElement;
        select.onchange = e =>
        {
            const elem = e.target as HTMLSelectElement;
            elem.style.color = (JSON.parse(elem.value) as Bridge.Suit).Color;
            (new HTMLPointsRow(elem)).#updateDealResults();
        };
        select.id = "contract-suit-selector";
        const cell = document.createElement("td") as HTMLTableCellElement;
        cell.appendChild(result);
        return cell;
    }

    static #createContractModifierSelector()
    {
        const column = CreateArraySelector(
            Bridge.ContractTypes,
            "contract-selector",
            Bridge.NamedEntity.TransformFunc
        );
        const selector = $(column).find('select')[0] as HTMLSelectElement;
        selector.value = JSON.stringify(Bridge.ContractType.Pass);
        selector.id = "contract-modifier-selector";
        selector.onchange = e => (new HTMLPointsRow(e.target as HTMLElement)).#updateDealResults();
        const cell = document.createElement("td") as HTMLTableCellElement;
        cell.appendChild(column);
        return cell;
    }

    static #createDealerSelector()
    {
        const result = CreateArraySelector(
            Bridge.ContractDealers,
            "contract-selector",
            Bridge.NamedEntity.TransformFunc
        );
        const select = $(result).find('select')[0] as HTMLSelectElement;
        select.onchange = e => (new HTMLPointsRow(e.target as HTMLElement)).#updateDealResults();
        select.id = "contract-dealer-selector";
        const cell = document.createElement("td") as HTMLTableCellElement;
        cell.appendChild(result);
        return cell;
    }

    static #createDealResultSelector()
    {
        const DealResults = ['-7', '-6', '-5', '-4', '-3', '-2', '-1', '&middot;', '+1', '+2', '+3', '+4', '+5', '+6'];
        const result = CreateArraySelector(DealResults, "", (dealResult: string) => dealResult);
        const selector = $(result).find('select')[0] as HTMLSelectElement;
        selector.disabled = true;
        selector.onchange = e => (new HTMLPointsRow(e.target as HTMLElement)).#updateDealResults();
        selector.id = "contract-result-selector";
        const cell = document.createElement("td") as HTMLTableCellElement;
        cell.appendChild(result);
        return cell;
    }

    static #createNumberColumn(number: number)
    {
        const cell = document.createElement("td") as HTMLTableCellElement;
        cell.style.verticalAlign = "middle";
        cell.innerHTML = number.toString();
        return cell;;
    }

    static #createVulnerabilityColumn(vulnerability: Bridge.Vulnerability)
    {
        const cell = document.createElement("td") as HTMLTableCellElement;
        cell.style.verticalAlign = "middle";
        cell.id = "vulnerability";
        cell.innerHTML = vulnerability.DisplayName;
        return cell;
    }

    static #createNextButtonColumn()
    {
        const cell = document.createElement("td") as HTMLTableCellElement;
        cell.className = "align-self-center";
        const button = document.createElement("button");
        button.className = "btn btn-primary fs-5 w-100";
        button.disabled = true;
        button.id = "next-btn";
        button.innerHTML = "&rarr;";
        button.onclick = () => nextDeal();
        cell.appendChild(button);
        return cell;
    }

    #descendantById<Type = HTMLElement>(id: string): Type
    {
        return this.#descendants("#" + id)[0] as Type;
    }
    #descendants(selector: string): JQuery
    {
        return $(this.PointsRow).find(selector);
    }
    #valueOfSelect<Type>(id: string): Type
    {
        return JSON.parse(this.#descendantById<HTMLSelectElement>(id).value) as Type;
    }

    #dealResultChanged()
    {
        const pcInput = this.#descendantById<HTMLInputElement>('points-row-pc');
        pcInput.disabled = false;
        const vuln = new Bridge.Vulnerability(this.#descendantById('vulnerability').innerHTML);
        const contractLevel = this.#valueOfSelect<number>("contract-level-selector");
        const contractSuit = this.#valueOfSelect<Bridge.Suit>("contract-suit-selector");
        const contractType = this.#valueOfSelect<Bridge.ContractType>("contract-modifier-selector");
        const contractDealer = this.#valueOfSelect<Bridge.ContractDealer>("contract-dealer-selector");
        const contract = new Bridge.Contract(contractLevel, contractSuit, contractType, contractDealer);
        const relativeTricks = +this.#valueOfSelect<number>("contract-result-selector") || 0;
        const takenPoints = this.#descendantById('points-row-taken-points');
        takenPoints.innerHTML = contract.Score(vuln, relativeTricks).toString();
    }

    #showIMPs(dealResults: Bridge.DealResults)
    {
        let dealerImpsElem: HTMLElement;
        let opponentsImpsElem: HTMLElement;
        if (Bridge.NamedEntity.IsEqual(Bridge.ContractDealer.North, dealResults.Dealer) ||
            Bridge.NamedEntity.IsEqual(Bridge.ContractDealer.South, dealResults.Dealer))
        {
            dealerImpsElem = this.#descendantById("points-row-NS");
            opponentsImpsElem = this.#descendantById("points-row-WE");
        } else
        {
            dealerImpsElem = this.#descendantById("points-row-WE");
            opponentsImpsElem = this.#descendantById("points-row-NS");
        }

        if (dealResults.IMPs > 0)
        {
            dealerImpsElem.innerText = dealResults.IMPs.toString();
            opponentsImpsElem.innerHTML = "&mdash;";
        } else if (dealResults.IMPs < 0)
        {
            dealerImpsElem.innerHTML = "&mdash;";
            opponentsImpsElem.innerText = (-dealResults.IMPs).toString();
        } else
        {
            dealerImpsElem.innerHTML = opponentsImpsElem.innerHTML = "&mdash;";
        }
    }

    #showDealResults()
    {
        const pc = +this.#descendantById<HTMLInputElement>("points-row-pc").value;
        const vuln = new Bridge.Vulnerability(this.#descendantById("vulnerability").innerHTML);
        const dealer = this.#valueOfSelect<Bridge.ContractDealer>("contract-dealer-selector");
        const score = +this.#descendantById("points-row-taken-points").innerText;

        const dealResults = new Bridge.DealResults(vuln, dealer, score, pc);
        this.#descendantById("points-row-compensation").innerText = dealResults.Compensation.toString();
        this.#descendantById("points-row-delta").innerText = dealResults.Delta.toString();
        this.#showIMPs(dealResults);
    }

    #updateDealResults()
    {
        this.#updateDealResultState();
        const dealResult = this.#descendantById<HTMLSelectElement>('contract-result-selector');
        if (dealResult.disabled || !dealResult.value) return;
        this.#dealResultChanged();
        const pcInput = this.#descendantById<HTMLInputElement>('points-row-pc');
        const pc = +pcInput.value;
        if (!pc) return;
        if (pc < 0 || pc > 40 || !Number.isInteger(pc))
        {
            this.#descendantById('points-row-pc-alert').hidden = false;
            (this.#descendantById('next-btn') as HTMLButtonElement).disabled = true;
            return;
        }
        this.#descendantById('points-row-pc-alert').hidden = true;
        this.#showDealResults();
        (this.#descendantById('next-btn') as HTMLButtonElement).disabled = false;
        showIMPsTotal();
    }

    #updateDealResultState()
    {
        const selectors = this.#descendants('.contract-selector');
        let hasNotSelected = false;
        $(selectors).each((_, select) =>
        {
            hasNotSelected = hasNotSelected || !(select as HTMLSelectElement).value;
        });
        this.#descendantById<HTMLSelectElement>('contract-result-selector').disabled = hasNotSelected;
    }

    static #createTextColumn(idSuffix: string)
    {
        const result = document.createElement("td") as HTMLTableCellElement;
        result.style.verticalAlign = "middle";
        result.className = "text-center";
        result.id = "points-row-" + idSuffix;
        result.innerHTML = "&mdash;";
        return result;
    }

    static #createPCColumn()
    {
        const result = document.createElement("td") as HTMLTableCellElement;
        result.className = "text-center";
        const input = document.createElement("input");
        input.className = "form-control fs-5";
        input.id = "points-row-pc";
        input.disabled = true;
        input.type = "number";
        input.min = "0";
        input.max = "40";
        input.onchange = (event) => (new HTMLPointsRow(event.target as HTMLElement)).#updateDealResults();
        result.appendChild(input);
        result.appendChild(HTMLPointsRow.#createPCAlertColumn());
        return result;
    }

    static #createPCAlertColumn()
    {
        const alert = document.createElement("div");
        alert.className = "text-danger fs-6";
        alert.innerText = Localization.InvalidPC;
        alert.id = "points-row-pc-alert";
        alert.hidden = true;
        return alert;
    }

    static Create(idx: number)
    {
        const rowElem = document.createElement("tr");
        rowElem.className = "text-center mb-2 points-row fs-5";
        const row = new HTMLPointsRow(rowElem);

        row.PointsRow.appendChild(HTMLPointsRow.#createNumberColumn(idx + 1));
        row.PointsRow.appendChild(HTMLPointsRow.#createVulnerabilityColumn(Bridge.Vulnerability.FromDealNumber(idx + 1)));
        row.PointsRow.appendChild(HTMLPointsRow.#createContractLevelSelector());
        row.PointsRow.appendChild(HTMLPointsRow.#createContractSuitSelector());
        row.PointsRow.appendChild(HTMLPointsRow.#createContractModifierSelector());
        row.PointsRow.appendChild(HTMLPointsRow.#createDealerSelector());
        row.PointsRow.appendChild(HTMLPointsRow.#createDealResultSelector());
        row.PointsRow.appendChild(HTMLPointsRow.#createTextColumn("taken-points"));
        row.PointsRow.appendChild(HTMLPointsRow.#createPCColumn());
        row.PointsRow.appendChild(HTMLPointsRow.#createTextColumn("compensation"));
        row.PointsRow.appendChild(HTMLPointsRow.#createTextColumn("delta"));
        row.PointsRow.appendChild(HTMLPointsRow.#createTextColumn("NS"));
        row.PointsRow.appendChild(HTMLPointsRow.#createTextColumn("WE"));
        row.PointsRow.appendChild(HTMLPointsRow.#createNextButtonColumn());
        return row;
    }
}

// --- Table and UI Utilities ---

function calcColumnTotal(selector: string): number
{
    let result = 0;
    $(selector).each(function ()
    {
        result += parseInt($(this)[0]!.innerText) || 0;
    });
    return result;
}

function showIMPsTotal(): void
{
    const nsTotal = calcColumnTotal('.points-row #points-row-NS');
    document.getElementById("imps-ns")!.innerText = nsTotal > 0 ? nsTotal.toString() : "";
    const weTotal = calcColumnTotal('.points-row #points-row-WE');
    document.getElementById("imps-we")!.innerText = weTotal > 0 ? weTotal.toString() : "";

    const diff = nsTotal - weTotal;
    if (diff > 0)
    {
        document.getElementById("imps-diff-ns")!.innerText = diff.toString();
        document.getElementById("imps-diff-we")!.innerText = "";
    } else if (diff < 0)
    {
        document.getElementById("imps-diff-ns")!.innerText = "";
        document.getElementById("imps-diff-we")!.innerText = (-diff).toString();
    } else
    {
        document.getElementById("imps-diff-ns")!.innerText = "";
        document.getElementById("imps-diff-we")!.innerText = "";
    }
}

function nextDeal(): void
{
    const table = document.getElementById("points-tbl") as HTMLElement;
    const rows = $(table).find('.points-row');
    rows.last().find('#next-btn').prop('hidden', true);
    insertNewPointsTableRow(rows.length);
}

function insertNewPointsTableRow(index: number): void
{
    const dividerRow = document.getElementById("divider");
    const newRow = HTMLPointsRow.Create(index);
    dividerRow!.parentNode!.insertBefore(newRow.PointsRow, dividerRow);
}

function setElementHTML(id: string, html: string): void
{
    const elem = document.getElementById(id);
    if (elem) elem.innerHTML = html;
    else console.error(`Element with id '${id}' not found.`);
}

function initElements(): void
{
    setElementHTML("start-btn", Localization.StartButton);
    setElementHTML("deal-number-header", Localization.DealNumberHeader);
    setElementHTML("vulnerability-header", Localization.VulnerabilityHeader);
    setElementHTML("contract-header", Localization.ContractHeader);
    setElementHTML("played-tricks-header", Localization.PlayedTricksHeader);
    setElementHTML("score-header", Localization.ScoreHeader);
    setElementHTML("pc-header", Localization.PCHeader);
    setElementHTML("compensation-header", Localization.CompensationHeader);
    setElementHTML("delta-header", Localization.DeltaHeader);
    setElementHTML("ns-imps-header", Localization.ImpsNSHeader);
    setElementHTML("we-imps-header", Localization.ImpsWEHeader);
    setElementHTML("imps-sum-header", Localization.ImpsSumHeader);
    setElementHTML("imps-diff-header", Localization.ImpsDiffHeader);
}

// --- Main Page Initialization ---

export function InitNewGamePage(): void
{
    for (const { prefix, label } of PLAYER_IDS)
    {
        createSideSelector(prefix, label());
    }
    updateStartButtonState();
    initElements();
    insertNewPointsTableRow(0);
    $("#start-btn").on("click", function (e)
    {
        e.preventDefault();
        $("#points-tbl").removeAttr("hidden");
        $("#north-selector, #south-selector, #west-selector, #east-selector").prop('disabled', true);
        $(this).prop('disabled', true);
    });
}